import { useEffect, useRef, useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import s from './FilterDropdown.module.css';

export type FilterOption<T extends string = string> = {
  value: T | 'all';
  label: string;
};

type FilterDropdownProps<T extends string> = {
  label: string;
  value: T | 'all';
  options: FilterOption<T>[];
  onChange: (v: T | 'all') => void;
};

function FilterDropdown<T extends string>({
  label,
  value,
  options,
  onChange,
}: FilterDropdownProps<T>) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const isActive = value !== 'all';
  const active = options.find((o) => o.value === value);

  return (
    <div ref={ref} className={s.filterWrap}>
      <button
        type="button"
        className={`${s.filterTrigger} ${isActive ? s.filterTriggerActive : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}>
        <span className={s.filterLabel}>{label}:</span>
        <span className={s.filterValue}>{active ? active.label : 'All'}</span>
        <KeyboardArrowDownIcon
          className={`${s.chevron} ${open ? s.chevronOpen : ''}`}
          sx={{ fontSize: 16 }}
        />
      </button>

      {open && (
        <div className={s.menu} role="listbox">
          {options.map((o) => {
            const selected = o.value === value;
            return (
              <div
                key={o.value}
                role="option"
                aria-selected={selected}
                className={`${s.menuItem} ${selected ? s.menuItemActive : ''}`}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}>
                <span className={s.menuItemLabel}>{o.label}</span>
                {selected && <CheckIcon className={s.menuCheck} sx={{ fontSize: 16 }} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default FilterDropdown;
